import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { ConfirmDialog } from '../../reports/edit/EditCommon';
import { EntryDateField, UntilField } from './EntryRuleFields';
import {
  KIND_HINTS, KIND_LABELS, formatDay, fromInputMoment, parseTypedDay, toInputMoment, validateDate, validateUntil
} from './revenueEntryCalc';
import type { EntryData, EntryKind, EntryRuleInput } from './revenueEntryApi';

interface EntryNetworkDialogProps {
  open: boolean;
  kind: EntryKind;
  /** Данные экрана; форма заполняется при монтировании: на каждое открытие свой key */
  data: EntryData;
  saving: boolean;
  /** Возвращает true, если параметр сохранен и форму можно закрыть */
  onSave: (kind: EntryKind, input: EntryRuleInput, confirm?: string) => Promise<boolean>;
  onClose: () => void;
}

/** Продление, разблокировка или дата ввода для всей сети; смена даты подтверждается повторным вводом даты */
const EntryNetworkDialog: React.FC<EntryNetworkDialogProps> = ({ open, kind, data, saving, onSave, onClose }) => {
  const { bounds, network, state } = data;
  const current = network[kind];

  // Действующее правило открывается со своими значениями, новое - со значениями по умолчанию
  const [until, setUntil] = useState(toInputMoment(current ? current.until : bounds.defaults[kind]));
  const [date, setDate] = useState(current && current.date ? current.date : state.date);
  const [typed, setTyped] = useState('');
  const [errors, setErrors] = useState({ until: '', date: '', typed: '' });

  const isDate = kind === 'date';
  const confirmed = !isDate || parseTypedDay(typed) === date;

  const submit = async () => {
    if (saving) return;
    const found = {
      until: validateUntil(until, bounds, kind),
      date: isDate ? validateDate(date, bounds) : '',
      typed: confirmed ? '' : `Введите дату ${formatDay(date)} в формате ДД.ММ.ГГГГ`
    };
    setErrors(found);
    if (found.until || found.date || found.typed) return;

    const input: EntryRuleInput = isDate ? { until: fromInputMoment(until), date } : { until: fromInputMoment(until) };
    if (await onSave(kind, input, isDate ? date : undefined)) onClose();
  };

  const hint = kind === 'extend' ? `Продлевается ввод за ${formatDay(bounds.extendDate)}. ${KIND_HINTS.extend}` : KIND_HINTS[kind];

  return (
    <ConfirmDialog
      open={open}
      title={`${KIND_LABELS[kind]}: вся сеть`}
      confirmText="Сохранить"
      loading={saving}
      maxWidth="xs"
      onConfirm={submit}
      onClose={onClose}
    >
      <Typography sx={{ color: '#64748b', fontSize: '0.86rem', mb: 2.5 }}>{hint}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {isDate && (
          <EntryDateField
            value={date}
            onChange={(value) => {
              setDate(value);
              setErrors((prev) => ({ ...prev, date: '', typed: '' }));
            }}
            bounds={bounds}
            error={errors.date}
          />
        )}
        <UntilField
          value={until}
          onChange={(value) => {
            setUntil(value);
            setErrors((prev) => ({ ...prev, until: '' }));
          }}
          bounds={bounds}
          kind={kind}
          error={errors.until}
        />
        {isDate && (
          <TextField
            size="small"
            fullWidth
            required
            label="Подтверждение даты"
            placeholder="ДД.ММ.ГГГГ"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              setErrors((prev) => ({ ...prev, typed: '' }));
            }}
            error={!!errors.typed}
            helperText={errors.typed || 'Дата меняется для всех аптек сети: введите ее еще раз вручную'}
            inputProps={{ maxLength: 10, autoComplete: 'off' }}
          />
        )}
      </Box>
    </ConfirmDialog>
  );
};

export default EntryNetworkDialog;
